import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, Users, CheckCircle2, Loader2, X, Sparkles, Clock } from 'lucide-react';
import { SectionHeader, EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { BACKEND_URL } from '@/config/api';
import { fmtMoney } from '@/lib/format';
import { useDashboard } from '@/context/DashboardContext';
import { BillingChannelPicker, LockedChannelNote, needsBillingChoice, lockedChannel } from '@/components/dashboard/BillingChannelPicker';

const day = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00`);
const fmtRange = (a, b) => {
  const o = { day: 'numeric', month: 'long' };
  const A = day(a); const B = day(b || a);
  return A.toDateString() === B.toDateString()
    ? A.toLocaleDateString('es-ES', { ...o, year: 'numeric' })
    : `${A.toLocaleDateString('es-ES', o)} – ${B.toLocaleDateString('es-ES', { ...o, year: 'numeric' })}`;
};
const fmtEventDate = (iso) => day(iso).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
const flyer = (u) => (u ? `${BACKEND_URL}${u}` : null);

const REG_LABEL = { approved: 'Plaza confirmada', pending: 'Solicitud en revisión', awaiting_payment: 'Pago pendiente' };

// ¿Prefieres hacerte Premium? — la membresía Premium incluye el bootcamp
function PremiumUpsell({ offer, price, currency, billing, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busyChannel, setBusyChannel] = useState(null);
  if (!offer) return null;
  const extra = offer.currency === currency ? offer.amount - price : null;
  const until = offer.early && offer.deadline ? new Date(offer.deadline).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' }) : null;
  // Upgrade de quien ya tiene una anual: misma vía de pago que su suscripción
  const locked = offer.upgrade ? lockedChannel(billing) : null;
  const pay = async (channel) => {
    setBusy(true);
    setBusyChannel(channel);
    try {
      const { checkout_url: url } = await dashboardApi.createCheckout('premium', channel);
      window.location.href = url;
    } catch (e) { toast.error(e.message); setBusy(false); setBusyChannel(null); }
  };
  const go = () => {
    if (needsBillingChoice(billing, offer.currency, !!locked)) setPicking(true);
    else pay(locked || 'particular');
  };
  return (
    <div className={`rounded-xl border border-[#c9a84c]/30 bg-[#c9a84c]/[0.07] ${compact ? 'p-4' : 'p-5'} space-y-2`} data-testid="bootcamp-upsell">
      <p className="text-white font-medium inline-flex items-center gap-2"><Sparkles size={15} className="text-[#c9a84c]" /> ¿Prefieres hacerte Premium?</p>
      <p className="text-sm text-gray-300">
        {offer.upgrade
          ? (extra == null
            ? `Pasa de ${offer.from_name} a Premium por ${fmtMoney(offer.amount, offer.currency)}${until ? ` (hasta el ${until})` : ''}, con el bootcamp incluido.`
            : extra < -1
            ? `Pasa de ${offer.from_name} a Premium por ${fmtMoney(offer.amount, offer.currency)}${until ? ` (hasta el ${until})` : ''}: menos que la plaza suelta, y el bootcamp queda incluido.`
            : Math.abs(extra) <= 1
              ? `Pasa de ${offer.from_name} a Premium por ${fmtMoney(offer.amount, offer.currency)}${until ? ` (hasta el ${until})` : ''}, ${extra === 0 ? 'lo mismo' : 'prácticamente lo mismo'} que la plaza suelta: el bootcamp queda incluido y además eres Premium.`
              : `Pasa de ${offer.from_name} a Premium por ${fmtMoney(offer.amount, offer.currency)}${until ? ` (hasta el ${until})` : ''}${extra > 0 ? `: solo ${fmtMoney(extra, currency)} más que la plaza suelta` : ''}, con el bootcamp incluido.`)
          : `La membresía Premium incluye tu plaza en el bootcamp y un año de Premium por ${fmtMoney(offer.amount, offer.currency)}${extra > 0 ? `: solo ${fmtMoney(extra, currency)} más que la plaza suelta.` : '.'}`}
      </p>
      {offer.keeps_lifetime && <p className="text-xs text-gray-400">Mantienes tu condición vitalicia.</p>}
      {locked && <LockedChannelNote billing={billing} amount={offer.amount} currency={offer.currency} />}
      <button type="button" onClick={go} disabled={busy} className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-50" data-testid="bootcamp-upsell-cta">
        {busy && <Loader2 size={14} className="animate-spin" />} Hazte Premium · {fmtMoney(offer.amount, offer.currency)}
      </button>
      <BillingChannelPicker open={picking} billing={billing} amount={offer.amount} currency={offer.currency}
        period={offer.upgrade ? null : '/ año'} title={offer.upgrade ? `Pasar de ${offer.from_name} a Premium` : 'Membresía Premium'}
        subtitle="¿Cómo quieres pagar?" busyChannel={busyChannel} onSelect={pay}
        onClose={() => { if (!busyChannel) setPicking(false); }} />
    </div>
  );
}

function BootcampForm({ bootcamp, offer, billing, onClose, onDone }) {
  const [form, setForm] = useState({ dietary_restrictions: '', motivation: '', comments: '' });
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busyChannel, setBusyChannel] = useState(null);
  const purchase = bootcamp.access === 'purchase';
  // Compra de la plaza: vía de pago (particular / empresa con IVA) y a Stripe
  const buy = async (channel) => {
    setBusy(true);
    setBusyChannel(channel);
    try {
      const { url } = await dashboardApi.checkoutBootcamp(bootcamp.id, form, channel);
      window.location.href = url;
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
      setBusyChannel(null);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (purchase) {
      if (needsBillingChoice(billing, bootcamp.currency)) setPicking(true);
      else buy('particular');
      return;
    }
    setBusy(true);
    try {
      await dashboardApi.reserveBootcamp(bootcamp.id, form);
      toast.success('¡Plaza reservada! Te enviaremos los detalles logísticos por email.');
      onDone();
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };
  const field = 'w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="bc-form-title">
      <motion.form onSubmit={submit} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0d1829] p-6 space-y-4" data-testid="bootcamp-form">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id="bc-form-title" className="text-white font-semibold">{purchase ? 'Comprar plaza' : 'Reservar mi plaza'}</h3>
            <p className="text-sm text-gray-400">{bootcamp.title} · {fmtRange(bootcamp.start_date, bootcamp.end_date)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar"><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        {purchase && <PremiumUpsell offer={offer} price={bootcamp.price} currency={bootcamp.currency} billing={billing} compact />}
        {purchase && offer && <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold pt-1">O compra solo la plaza</p>}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block" htmlFor="bc-diet">Alergias o intolerancias</label>
          <input id="bc-diet" value={form.dietary_restrictions} onChange={(e) => setForm({ ...form, dietary_restrictions: e.target.value })} className={field} placeholder="Opcional" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block" htmlFor="bc-mot">¿Qué esperas del bootcamp?</label>
          <textarea id="bc-mot" rows={3} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} className={`${field} resize-none`} placeholder="Opcional" />
        </div>
        <p className="text-xs text-gray-400">Alojamiento y dietas no incluidos. Recibirás los detalles logísticos por email.</p>
        <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#c9a84c] py-3 text-sm font-semibold text-[#0a1628] disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="bootcamp-submit">
          {busy && <Loader2 size={15} className="animate-spin" />}
          {purchase ? `Pagar ${fmtMoney(bootcamp.price, bootcamp.currency)}` : 'Confirmar reserva'}
        </button>
      </motion.form>
      {purchase && (
        <BillingChannelPicker open={picking} billing={billing} amount={bootcamp.price} currency={bootcamp.currency}
          title={`Plaza ${bootcamp.title || 'Bootcamp Broker Digital'}`} subtitle="¿Cómo quieres pagar?"
          busyChannel={busyChannel} onSelect={buy} onClose={() => { if (!busyChannel) setPicking(false); }} />
      )}
    </div>
  );
}

export default function DashboardEventos() {
  const { refreshMembership } = useDashboard();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [joining, setJoining] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    dashboardApi.memberEvents().then(setData).catch((e) => { toast.error(e.message); setData({ events: [], bootcamps: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const st = params.get('bootcamp');
    if (!st) return;
    if (st === 'success') { toast.success('Pago recibido. Tu plaza se confirma en unos segundos.'); [2000, 6000].forEach((ms) => setTimeout(load, ms)); }
    if (st === 'cancel') toast.info('Pago cancelado. Tu plaza no se ha reservado.');
    setParams({}, { replace: true });
    refreshMembership?.();
  }, [params, setParams, load, refreshMembership]);

  const join = async (ev, ticket) => {
    setJoining(ev.slug);
    try {
      await dashboardApi.joinEvent(ev.slug, ticket);
      toast.success(ticket === 'cocktail' ? 'Apuntado. El equipo te enviará cómo pagar el cóctel.' : '¡Apuntado! Te esperamos.');
      load();
    } catch (e) { toast.error(e.message); } finally { setJoining(null); }
  };

  if (!data) return <PageSkeleton cards={0} rows={5} />;

  if (data.locked && !data.bootcamps?.length) {
    return (
      <div className="space-y-6" data-testid="dashboard-eventos">
        <SectionHeader title="Eventos y bootcamp" subtitle="Encuentros del club y Bootcamp Broker Digital" />
        <EmptyState icon={CalendarDays} title="Los eventos son para socios"
          text="Activa tu membresía para ver el calendario del club y reservar tu plaza en los encuentros y en el Bootcamp."
          action={<Link to="/dashboard#membresias" className="inline-flex rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628]">Ver membresías</Link>} />
      </div>
    );
  }

  const nothing = !data.events.length && !data.bootcamps.length && !data.events_locked;
  return (
    <div className="space-y-8" data-testid="dashboard-eventos">
      <SectionHeader title="Eventos y bootcamp" subtitle="Reserva tu plaza en los encuentros del club y en el Bootcamp Broker Digital" />

      {nothing && <EmptyState icon={CalendarDays} title="No hay fechas abiertas ahora mismo" text="En cuanto publiquemos el próximo encuentro o edición del Bootcamp aparecerá aquí." />}

      {data.bootcamps.length > 0 && (
        <section aria-labelledby="bc-title" className="space-y-4">
          <h2 id="bc-title" className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Bootcamp Broker Digital</h2>
          {data.bootcamps.map((b) => {
            const reg = b.my_registration;
            const full = b.available_spots <= 0;
            return (
              <article key={b.id} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden md:flex" data-testid="bootcamp-card">
                {b.flyer_url && <img src={flyer(b.flyer_url)} alt="" className="md:w-56 h-44 md:h-auto object-cover" />}
                <div className="p-5 flex-1 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-white text-lg font-semibold">{b.title}</h3>
                      <p className="text-sm text-gray-400 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} /> {fmtRange(b.start_date, b.end_date)}</span>
                        {(b.location || b.city) && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {[b.location, b.city].filter(Boolean).join(', ')}</span>}
                        <span className="inline-flex items-center gap-1.5"><Users size={14} /> {full ? 'Completo' : b.available_spots === 1 ? '1 plaza libre' : `${b.available_spots} plazas libres`}</span>
                      </p>
                    </div>
                    {b.access === 'included' && <span className="rounded-full border border-[#c9a84c]/30 bg-[#c9a84c]/10 px-3 py-1 text-xs text-[#c9a84c]">Incluido en tu Premium</span>}
                  </div>
                  {b.description && <p className="text-sm text-gray-300 leading-relaxed">{b.description}</p>}
                  {reg && reg.status !== 'awaiting_payment' ? (
                    <p className="inline-flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={16} /> {REG_LABEL[reg.status] || 'Inscrito'}</p>
                  ) : full ? (
                    <p className="text-sm text-gray-400">No quedan plazas en esta edición.</p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <button onClick={() => setForm(b)} className="rounded-xl bg-[#c9a84c] px-5 py-2.5 text-sm font-semibold text-[#0a1628]" data-testid="bootcamp-cta">
                        {b.access === 'included' ? 'Reservar mi plaza' : `Comprar plaza · ${fmtMoney(b.price, b.currency)}`}
                      </button>
                      {b.access === 'purchase' && data.premium_offer && <span className="text-xs text-gray-400">o hazte Premium y la tienes incluida</span>}
                      {reg?.status === 'awaiting_payment' && <span className="text-xs text-amber-200">Tienes un pago sin completar</span>}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}

      {data.events_locked && (
        <p className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-sm text-gray-400">
          Los encuentros del club son para socios. <Link to="/dashboard#membresias" className="text-[#c9a84c] underline underline-offset-2">Ver membresías</Link>
        </p>
      )}

      {data.events.length > 0 && (
        <section aria-labelledby="ev-title" className="space-y-4">
          <h2 id="ev-title" className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Encuentros del club</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {data.events.map((ev) => (
              <article key={ev.slug} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid="event-card">
                {ev.flyer_url && <img src={flyer(ev.flyer_url)} alt="" className="w-full h-40 object-cover" />}
                <div className="p-5 space-y-3">
                  <h3 className="text-white font-semibold">{ev.title}</h3>
                  {ev.subtitle && <p className="text-sm text-gray-400">{ev.subtitle}</p>}
                  <p className="text-sm text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1.5 capitalize"><CalendarDays size={14} /> {fmtEventDate(ev.event_date)}</span>
                    {ev.start_time && <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {ev.start_time} h</span>}
                    {(ev.location_name || ev.city) && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {[ev.location_name, ev.city].filter(Boolean).join(', ')}</span>}
                  </p>
                  {ev.my_registration ? (
                    <p className="inline-flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={16} /> Estás apuntado</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <button disabled={joining === ev.slug} onClick={() => join(ev, 'libre')} className="rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-50">Apuntarme</button>
                      {ev.cocktail_enabled && (
                        <button disabled={joining === ev.slug} onClick={() => join(ev, 'cocktail')} className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white disabled:opacity-50">
                          Con cóctel{ev.cocktail_price_eur ? ` · ${fmtMoney(ev.cocktail_price_eur, 'eur')}` : ''}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {form && <BootcampForm bootcamp={form} offer={data.premium_offer} billing={data.billing} onClose={() => setForm(null)} onDone={() => { setForm(null); load(); }} />}
    </div>
  );
}
